import React, { useCallback } from 'react'
import { Icon } from '@/components/ui/icon'
import type { ShaderError } from '@/lib/canvas/shader/types'

const BADGE_CLASS = 'rounded bg-black/60 px-1.5 py-0.5'
// The preview is always dark (bg-zinc-950), whatever the theme, so its text uses fixed light tones.
const LINK_CLASS = 'pointer-events-auto text-zinc-100 underline decoration-dotted underline-offset-2 hover:text-primary'

interface ShaderPreviewOverlaysProps {
  hasCode: boolean
  ready: boolean
  /** A compile, runtime or input error that replaces the picture. */
  blockingError: ShaderError | null
  /** A hung sketch keeps its last frame and gets a badge instead. */
  unresponsiveMessage?: string
  paused: boolean
  inputPreparing: boolean
  justCaptured: boolean
  onWriteCode: () => void
}

/** Everything drawn over the live preview: the empty state, start-up, errors and status badges. */
export function ShaderPreviewOverlays({
  hasCode,
  ready,
  blockingError,
  unresponsiveMessage,
  paused,
  inputPreparing,
  justCaptured,
  onWriteCode,
}: ShaderPreviewOverlaysProps) {
  const handleCode = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    onWriteCode()
  }, [onWriteCode])

  return (
    <>
      {!hasCode && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-zinc-950 px-6 text-center text-zinc-400">
          <Icon name="zap" className="h-5 w-5 opacity-40" />
          <span className="text-[11px] text-zinc-200">Describe a visual effect or write GLSL.</span>
          <span className="text-[10px] leading-relaxed">
            The preview runs what you write.{' '}
            <button type="button" className={LINK_CLASS} onClick={handleCode}>write code</button>
          </span>
        </div>
      )}

      {hasCode && !ready && !blockingError && (
        <div className="absolute inset-0 flex items-center justify-center bg-zinc-950/70 backdrop-blur-sm">
          <span className="text-[10px] text-zinc-400">Starting…</span>
        </div>
      )}

      {blockingError && (
        <div className="absolute inset-0 overflow-auto bg-red-950/85 p-2">
          <div className="text-[10px] font-medium text-red-200">
            {blockingError.kind === 'input' ? 'Input error' : 'Shader error'}
          </div>
          <pre className="mt-1 whitespace-pre-wrap break-words font-mono text-[10px] leading-snug text-red-100">
            {blockingError.line ? `line ${blockingError.line}: ` : ''}
            {blockingError.message}
          </pre>
        </div>
      )}

      <div className="absolute left-1.5 top-1.5 flex gap-1 font-mono text-[9px]">
        {paused && <span className={`${BADGE_CLASS} text-yellow-300`}>PAUSED</span>}
        {unresponsiveMessage !== undefined && <span className={`${BADGE_CLASS} text-orange-300`} title={unresponsiveMessage}>UNRESPONSIVE</span>}
        {inputPreparing && <span className={`${BADGE_CLASS} text-zinc-300`}>LOADING INPUT</span>}
        {justCaptured && <span className={`${BADGE_CLASS} text-emerald-300`}>CAPTURED</span>}
      </div>
    </>
  )
}
